import React, { useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Container,
  LinearProgress,
  Paper,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import LineItemV2 from "../../common/component/lineitem/LineItemV2";
import UserAvatar from "../../common/UserAvatar";
import { ExpandMoreOutlined, Search } from "@mui/icons-material";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import { STORAGE_BUCKET } from "../../common/constants";

export default function DraftOrders(props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  let location = useLocation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterQuery, setFilterQuery] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);

  useEffect(() => {
    setLoading(true);
    bkstApi.get(`/my/cart?filter=draft`).then((res) => {
      const sortedOrders = res.data.orders.sort((o1, o2) => o2.ts - o1.ts);
      setOrders(sortedOrders);
      setFilteredOrders(sortedOrders);
      setLoading(false);
    });
  }, [location.key]);

  // Filter orders based on search query
  useEffect(() => {
    if (!filterQuery.trim()) {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(
        (order) => order.title && order.title.toLowerCase().includes(filterQuery.toLowerCase())
      );
      setFilteredOrders(filtered);
    }
  }, [filterQuery, orders]);

  const clearDraft = (id) => {
    bkstApi.delete(`/my/cart/${id}`).then(() => {
      setOrders(orders.filter((o) => o.id !== id));
    });
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth={"sm"}>
        <Box mt={4} mb={2}>
          <Box>
            <Typography variant="h1" gutterBottom>
              Draft Orders
            </Typography>
            <Typography variant="body1" color="textSecondary">
              View draft orders saved in the last 24 hours. Click to expand, view items and send to
              POS
            </Typography>
          </Box>
        </Box>
        <Box my={2}>
          <TextField
            fullWidth
            placeholder="Search by title..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>
        <Box>
          {filteredOrders.map((o) => (
            <Box key={o.id} my={2}>
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <UserAvatar uid={o.uid} style={{ width: "50px", height: "50px" }} />
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {o.title}
                      </Typography>
                      <Typography variant="overline">
                        {o.items.length} item(s) • {moment(o.ts).fromNow()}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box mb={4}>
                    {o.items.map((i, idx) => {
                      return (
                        <Box p={1} my={1} key={idx} sx={{ backgroundColor: "#F7F7F7" }}>
                          <LineItemV2 item={i} view="min" />
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: "flex", gap: "10px" }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={() => clearDraft(o.id)}
                    >
                      Delete Draft
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      component={RouterLink}
                      to="/pos"
                      state={{
                        items: o.items,
                      }}
                    >
                      Send To POS
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
          {filteredOrders.length === 0 && (
            <Box my={2} px={2} pb={2} textAlign={"center"} sx={{ border: "1px solid #DDD" }}>
              <img
                style={{ width: "250px" }}
                src={`${STORAGE_BUCKET}/img/icon/search-results.svg`}
              />
              <Typography variant="subtitle1">Nothing to show</Typography>
            </Box>
          )}
        </Box>
      </Container>
    );
  }
}
